import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import { Button } from "@tubo/ui/components/ui/button";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import {
  useSemanticNamespaceList,
  useSemanticNamespaceUpdate,
  useSemanticNamespaceDelete,
  getSemanticNamespaceListQueryKey,
} from "@tubo/api-models/hooks/semantic-namespace-api.hooks";
import type { SemanticNamespaceDO } from "@tubo/api-models/hooks/semantic-namespace-api.schemas";
import {
  NamespaceCreateDialog,
  namespaceErrorMessage,
  NS_CONTEXT_GLOBAL,
  isGlobalNamespace,
  isPackNamespace,
} from "@tubo/semantic-concepts";
import { NAMESPACE_LABEL } from "@client/components/semantic-platform/namespace-label";
import {
  SemanticAssetPackageExportActions,
  SemanticAssetPackagePanel,
} from "@client/components/semantic-platform/semantic-asset-package-panel";

export const Route = createFileRoute("/_app/semantic-platform/namespaces/")({
  component: NamespaceManagePage,
});

const PAGE_SIZE = 20;

/** 管理页行：真实领域 + 合成的「全局」行统一视图形态 */
interface NamespaceRow {
  id: string;
  code: string;
  name: string;
  desc?: string;
  conceptCount?: number;
  vocabularyCount?: number;
  modelCount?: number;
  quantityKindCount?: number;
  source: "system" | "user" | "pack";
}

const GLOBAL_ROW: NamespaceRow = {
  id: NS_CONTEXT_GLOBAL,
  code: "global",
  name: "全局",
  desc: "未归属具体领域的资产；可被所有领域引用的共享层",
  source: "system",
};

/** 挂载资产总数（四类语义资产引用计数之和；删除守卫依据） */
function usageTotal(ns: NamespaceRow): number {
  return (
    (ns.conceptCount ?? 0) +
    (ns.vocabularyCount ?? 0) +
    (ns.modelCount ?? 0) +
    (ns.quantityKindCount ?? 0)
  );
}

function NamespaceManagePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<SemanticNamespaceDO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SemanticNamespaceDO | null>(null);
  const [packageOpen, setPackageOpen] = useState(false);

  const { data: namespaces = [], isLoading } = useSemanticNamespaceList({ query: {} });

  const rows = useMemo<NamespaceRow[]>(() => {
    const globalNamespace = namespaces.find((ns) => isGlobalNamespace(ns.code));
    const mapped = namespaces
      .filter((ns) => !isGlobalNamespace(ns.code))
      .map((ns): NamespaceRow => ({
        ...ns,
        source: isPackNamespace(ns.code) ? "pack" : "user",
      }));
    // 全局行恒在首位；行业包领域排在其后（随包装卸的托管容器）
    return [{ ...GLOBAL_ROW, ...globalNamespace }, ...mapped];
  }, [namespaces]);

  const total = rows.length;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(total / pageSize)));
  const pagedData = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getSemanticNamespaceListQueryKey() });

  /** 进入领域：设置上下文并跳概念管理页 */
  const enterDomain = (row: NamespaceRow) => {
    navigate({
      to: "/semantic-platform/concepts",
      search: row.id === NS_CONTEXT_GLOBAL ? {} : { ns: row.id },
    });
  };

  const deleteMut = useSemanticNamespaceDelete({
    mutation: {
      onSuccess: () => {
        toast.success(`${NAMESPACE_LABEL}已删除`);
        invalidate();
        setDeleteTarget(null);
      },
      onError: (err) => toast.error(namespaceErrorMessage(err, "删除失败")),
    },
  });

  const sourceBadge = (source: NamespaceRow["source"]) => {
    if (source === "system") return <Badge variant="secondary">系统</Badge>;
    if (source === "pack")
      return (
        <Badge variant="outline" className="gap-1">
          <Lock className="size-3" /> 行业包
        </Badge>
      );
    return <span className="text-xs text-muted-foreground">—</span>;
  };

  const columns: DataTableColumn<NamespaceRow>[] = [
    {
      id: "name",
      header: "名称",
      cell: (r) => (
        <span className="flex items-center gap-1.5 font-medium">
          <span className="min-w-0 truncate">{r.name}</span>
          {r.source === "pack" && <Lock className="size-3 shrink-0 text-muted-foreground" />}
        </span>
      ),
    },
    { id: "code", header: "编码", cell: (r) => <span className="font-mono text-sm">{r.code}</span> },
    {
      id: "usage",
      header: "挂载资产",
      cell: (r) => (
        <span className="text-xs text-muted-foreground">
          概念 {r.conceptCount ?? "—"} · 字典 {r.vocabularyCount ?? "—"} · 模型 {r.modelCount ?? "—"} · 度量 {r.quantityKindCount ?? "—"}
        </span>
      ),
    },
    {
      id: "desc",
      header: "说明",
      cell: (r) => <span className="block max-w-64 truncate text-xs text-muted-foreground">{r.desc ?? "—"}</span>,
    },
    { id: "source", header: "来源", cell: (r) => sourceBadge(r.source) },
    {
      id: "actions",
      header: "操作",
      cell: (r) => (
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => enterDomain(r)}>
            进入
          </Button>
          {r.source === "user" && (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="重命名"
                title="重命名"
                onClick={() => setRenameTarget(namespaces.find((ns) => ns.id === r.id) ?? null)}
              >
                <Pencil className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="删除"
                title={usageTotal(r) > 0 ? `仍有 ${usageTotal(r)} 项资产挂载，请先移动或清除归属` : "删除"}
                className="text-destructive"
                disabled={usageTotal(r) > 0}
                onClick={() => setDeleteTarget(namespaces.find((ns) => ns.id === r.id) ?? null)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </>
          )}
          <SemanticAssetPackageExportActions namespaceId={r.id} name={r.code} />
        </div>
      ),
    },
  ];

  const headerTitle = useMemo(
    () => <span className="text-sm font-semibold">{NAMESPACE_LABEL}管理</span>,
    [],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 size-4" /> 新建{NAMESPACE_LABEL}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setPackageOpen(true)}>
          迁移语义资产包
        </Button>
      </div>
    ),
    [],
  );
  useSecondaryNavActions(headerActions);

  return (
    <div className="h-full min-h-0 min-w-0">
      <DataTable
        data={pagedData}
        columns={columns}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        page={currentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        total={total}
        onPageChange={setPage}
        emptyState={
          <div className="py-6 text-center text-sm text-muted-foreground">
            <p>暂无{NAMESPACE_LABEL}</p>
            <p className="mt-1 text-xs">
              {NAMESPACE_LABEL}用于组织概念/数据字典/数据模型/度量；未归属的资产位于「全局」，点击右上角「新建{NAMESPACE_LABEL}」开始
            </p>
          </div>
        }
      />

      <NamespaceCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <NamespaceRenameDialog target={renameTarget} onClose={() => setRenameTarget(null)} />
      <NamespaceDeleteDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={(id) => deleteMut.mutate({ id })}
        pending={deleteMut.isPending}
      />
      <SemanticAssetPackagePanel
        open={packageOpen}
        onOpenChange={setPackageOpen}
        onImported={invalidate}
        namespaces={namespaces.map((namespace) => ({ id: namespace.id, code: namespace.code, name: namespace.name }))}
      />
    </div>
  );
}

/** 重命名/说明更新（编码为归属标识，不可改） */
function NamespaceRenameDialog({
  target,
  onClose,
}: {
  target: SemanticNamespaceDO | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  // 弹窗复用：切换目标时同步初始值
  const [syncedId, setSyncedId] = useState<string | null>(null);
  if (target && syncedId !== target.id) {
    setSyncedId(target.id);
    setName(target.name);
    setDesc(target.desc ?? "");
  }

  const updateMut = useSemanticNamespaceUpdate({
    mutation: {
      onSuccess: () => {
        toast.success(`${NAMESPACE_LABEL}已更新`);
        queryClient.invalidateQueries({ queryKey: getSemanticNamespaceListQueryKey() });
        onClose();
      },
      onError: (err) => toast.error(namespaceErrorMessage(err, "更新失败")),
    },
  });

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>重命名{NAMESPACE_LABEL}</DialogTitle>
          <DialogDescription>编码 {target?.code} 不可修改</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ns-rename-name">名称</Label>
            <Input
              id="ns-rename-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`${NAMESPACE_LABEL}名称`}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ns-rename-desc">说明</Label>
            <Input
              id="ns-rename-desc"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="用途说明（可选）"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            取消
          </Button>
          <Button
            size="sm"
            disabled={!name.trim() || updateMut.isPending}
            onClick={() =>
              target &&
              updateMut.mutate({ id: target.id, data: { name: name.trim(), desc: desc.trim() || undefined } })
            }
          >
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** 删除确认（仍有资产挂载时前端即拦截；后端守卫兜底） */
function NamespaceDeleteDialog({
  target,
  onClose,
  onConfirm,
  pending,
}: {
  target: SemanticNamespaceDO | null;
  onClose: () => void;
  onConfirm: (id: string) => void;
  pending: boolean;
}) {
  const total = target
    ? (target.conceptCount ?? 0) + (target.vocabularyCount ?? 0) + (target.modelCount ?? 0) + (target.quantityKindCount ?? 0)
    : 0;
  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>删除{NAMESPACE_LABEL}</DialogTitle>
          <DialogDescription>
            确认删除「{target?.name}」（{target?.code}）？该操作不可恢复。
          </DialogDescription>
        </DialogHeader>
        {total > 0 && (
          <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            该{NAMESPACE_LABEL}仍有 {total} 项语义资产挂载，请先移动或清除归属后再删除。
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={total > 0 || pending}
            onClick={() => target && onConfirm(target.id)}
          >
            删除
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
